import { isAccommodationV2Item } from 'lib/checkout/checkoutUtils'
import { floatify } from 'lib/maths/mathUtils'

export const ACCOMMODATION_V2_ACCEPT_RETRY_CAP = 3

export type AccommodationV2BlockingOutcome = Extract<
  App.AccommodationCheckoutEnquiryOutcome,
  'price_changed' | 'unavailable'
>

export interface AccommodationV2OutcomeRow {
  itemId: string
  sessionId: string
  roomLabel: string
  occupancy: App.Occupants
  outcome: AccommodationV2BlockingOutcome
  unavailableReason?: App.AccommodationCheckoutEnquiryUnavailableReason
  oldPrice: number
  newPrice: number
  oldPropertyFees: number
  newPropertyFees: number
}

export function isAccommodationV2BlockingOutcome(
  outcome: App.AccommodationCheckoutEnquiryOutcome | undefined,
): outcome is AccommodationV2BlockingOutcome {
  return outcome === 'price_changed' || outcome === 'unavailable'
}

function ticketTotal(tickets: Array<{ price?: number, ticketPrice?: number }> | undefined): number {
  return floatify((tickets ?? []).reduce((sum, ticket) => sum + (ticket.price ?? ticket.ticketPrice ?? 0), 0))
}

function shoppedBundleAddOn(session: App.AccommodationEnquirySessionData | undefined): number {
  return floatify(
    ticketTotal(session?.bundle?.tickets)
    + (session?.optInBundles ?? []).reduce((sum, bundle) => sum + ticketTotal(bundle.tickets), 0),
  )
}

function liveOptInAddOn(
  rate: App.RawPropertyRate | undefined,
  session: App.AccommodationEnquirySessionData | undefined,
): number {
  return floatify((session?.optInBundles ?? []).reduce((sum, selected) => {
    const live = rate?.optionalBundles?.find(bundle => bundle.bundleId === selected.bundleId)
    return sum + (live ? live.totalPrice : ticketTotal(selected.tickets))
  }, 0))
}

function liveBundleAddOn(
  rate: App.RawPropertyRate | undefined,
  session: App.AccommodationEnquirySessionData | undefined,
): number {
  const mandatory = ticketTotal(rate?.bundle?.tickets) || ticketTotal(session?.bundle?.tickets)
  return floatify(mandatory + liveOptInAddOn(rate, session))
}

function shoppedLuxPlusTotal(
  record: NonNullable<App.CheckoutState['accommodationSessions'][string]>,
): number | undefined {
  const fromChange = record.priceChange?.luxPlusTotal?.original
  if ((fromChange ?? 0) > 0) return fromChange
  const fromSession = record.data?.data.pricing.luxPlusTotal
  return (fromSession ?? 0) > 0 ? fromSession : undefined
}

function publicHotelSell(record: NonNullable<App.CheckoutState['accommodationSessions'][string]>): {
  oldPrice: number
  newPrice: number
} {
  const lockedSell = record.data?.data.pricing.sellTotal ?? 0

  return {
    oldPrice: record.priceChange?.sell.original ?? lockedSell,
    newPrice: record.priceChange?.sell.selected
      ?? record.rate?.bundle?.hotelSellingPrice
      ?? record.rate?.totals.sellingPrice
      ?? lockedSell,
  }
}

function roomPrice(record: NonNullable<App.CheckoutState['accommodationSessions'][string]>): {
  oldPrice: number
  newPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const sessionPricing = record.data?.data.pricing
  const lockedFees = sessionPricing?.propertyFees ?? 0
  const priceChange = record.priceChange
  const oldPropertyFees = floatify(priceChange?.propertyFees.original ?? lockedFees)
  const newPropertyFees = floatify(priceChange?.propertyFees.selected ?? record.rate?.totals.propertyFees ?? lockedFees)
  const shoppedLuxPlus = shoppedLuxPlusTotal(record)
  const sell = publicHotelSell(record)
  const sessionData = record.data?.data
  const shoppedAddOn = shoppedBundleAddOn(sessionData)
  const liveAddOn = liveBundleAddOn(record.rate, sessionData)

  // Enquiry can flag price_changed for a LuxPlus-only move while public sell stays
  // flat. Show the member amount the customer actually agreed to, and if LP is
  // withdrawn fall through to the public they will now pay. Prefer the enquiry
  // figure so a withdrawn LP is not read back off a stale hotel snapshot.
  const selectedLuxPlus = priceChange?.luxPlusTotal?.selected
    ?? record.rate?.bundle?.hotelLuxPlusSellingPrice
    ?? record.rate?.luxPlusTotals?.sellingPrice
  const oldHotel = shoppedLuxPlus ?? sell.oldPrice
  const newHotel = shoppedLuxPlus == null
    ? sell.newPrice
    : selectedLuxPlus && selectedLuxPlus > 0 ? selectedLuxPlus : sell.newPrice
  const oldPrice = floatify(oldHotel + shoppedAddOn)
  const newPrice = floatify(newHotel + liveAddOn)

  if (record.outcome === 'unavailable') {
    return {
      oldPrice,
      newPrice: 0,
      oldPropertyFees,
      newPropertyFees: 0,
    }
  }

  // Available / fallback rooms keep their locked session price. Only price_changed rooms
  // are re-sessioned at the live rate, so using record.rate here would invent a change.
  if (record.outcome === 'price_changed') {
    return {
      oldPrice,
      newPrice,
      oldPropertyFees,
      newPropertyFees,
    }
  }

  return {
    oldPrice,
    newPrice: oldPrice,
    oldPropertyFees: floatify(lockedFees),
    newPropertyFees: floatify(lockedFees),
  }
}

export function getAccommodationV2OutcomeRows(
  items: Array<App.Checkout.AccommodationV2Item>,
  sessions: App.CheckoutState['accommodationSessions'],
  roomLabels: Record<string, string> = {},
): Array<AccommodationV2OutcomeRow> {
  return items.flatMap((item) => {
    if (!item.sessionId) return []
    const record = sessions[item.sessionId]
    if (!record) return []
    if (!isAccommodationV2BlockingRecord(record)) {
      return []
    }

    return [{
      itemId: item.itemId,
      sessionId: item.sessionId,
      roomLabel: roomLabels[item.itemId] || record.rate?.name || '',
      occupancy: item.occupancy,
      outcome: record.outcome,
      unavailableReason: record.unavailableReason,
      ...roomPrice(record),
    }]
  })
}

function isAccommodationV2BlockingRecord(
  record: NonNullable<App.CheckoutState['accommodationSessions'][string]>,
): record is NonNullable<App.CheckoutState['accommodationSessions'][string]> & {
  outcome: AccommodationV2BlockingOutcome
} {
  return isAccommodationV2BlockingOutcome(record.outcome)
}

export function getAccommodationV2BlockingRows(
  rows: Array<AccommodationV2OutcomeRow>,
): Array<AccommodationV2OutcomeRow> {
  return rows.filter(row => isAccommodationV2BlockingOutcome(row.outcome))
}

export function sumOutcomeRowTotals(rows: Array<AccommodationV2OutcomeRow>, field: 'old' | 'new'): number {
  return floatify(rows.reduce((sum, row) => {
    if (field === 'new' && row.outcome === 'unavailable') return sum
    return sum + (field === 'old' ? row.oldPrice + row.oldPropertyFees : row.newPrice + row.newPropertyFees)
  }, 0))
}

export function getAccommodationV2CartTotals(
  items: Array<App.Checkout.AccommodationV2Item>,
  sessions: App.CheckoutState['accommodationSessions'],
): { oldCartTotal: number, newCartTotal: number } {
  const totals = items.reduce((totals, item) => {
    if (!item.sessionId) return totals
    const record = sessions[item.sessionId]
    if (!record) return totals
    const prices = roomPrice(record)
    return {
      oldCartTotal: totals.oldCartTotal + prices.oldPrice + prices.oldPropertyFees,
      newCartTotal: totals.newCartTotal + (
        record.outcome === 'unavailable' ? 0 : prices.newPrice + prices.newPropertyFees
      ),
    }
  }, { oldCartTotal: 0, newCartTotal: 0 })

  return {
    oldCartTotal: floatify(totals.oldCartTotal),
    newCartTotal: floatify(totals.newCartTotal),
  }
}

export function isAccommodationV2PurchaseBlocked(
  items: Array<App.Checkout.AnyItem>,
  sessions: App.CheckoutState['accommodationSessions'],
): boolean {
  return items.some((item) => {
    if (!isAccommodationV2Item(item)) return false
    if (!item.sessionId) return true
    const record = sessions[item.sessionId]
    if (!record || record.fetching || record.error || !record.outcome) return true
    return isAccommodationV2BlockingRecord(record)
  })
}

export function hasAccommodationV2SessionError(
  items: Array<App.Checkout.AnyItem>,
  sessions: App.CheckoutState['accommodationSessions'],
): boolean {
  return items.some((item) => {
    if (!isAccommodationV2Item(item) || !item.sessionId) return false
    return !!sessions[item.sessionId]?.error
  })
}

export function getAccommodationV2ReturnOfferView<T extends { itemViews: Array<{ item: { itemId: string } }> }>(
  itemViews: Array<T>,
  blockingItemIds: Array<string>,
): T | undefined {
  const blockingIds = new Set(blockingItemIds)
  return itemViews.find(view =>
    view.itemViews.some(itemView => blockingIds.has(itemView.item.itemId)),
  ) ?? itemViews[0]
}
